import { useRef } from 'react';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { Lightfall } from '@/components/lightfall';
import { LineMask } from '@/components/line-mask';
import { Reveal } from '@/components/reveal';
import { Section } from '@/components/section';
import { Button } from '@/components/ui/button';
import { copy } from '@/content/copy';
import { STATS } from '@/content/facts';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';
import { useSectionLink } from '@/scroll/use-scroll';

/**
 * The React Bits Lightfall preset chosen for the brand (BG Studio, 2026-09-22): the default
 * pale streak plus two blues near the logo's, falling over the page background (#0b0d1e, see
 * --background). Every other control sits at the component default: speed 0.5, one streak
 * layer, width 1, length 1, density 0.6, twinkle 1, glow 1, background glow 0.5, zoom 3.
 */
const STREAK_COLORS = ['#a6c8ff', '#186ac2', '#1c3e62'];
const CANVAS_GROUND = '#0b0d1e';

/**
 * One full-bleed stage: the shader is the ground of the whole viewport and the copy sits
 * over it behind a soft scrim. Nothing pins here; the page scrolls straight into the coins
 * section, whose stage carries the opening frame.
 */
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const sectionLink = useSectionLink();
  const [firstLine, secondLine] = copy.hero.title;

  // In view at mount, so this plays as an entrance.
  useRevealOnEnter(
    ref,
    (tl, q) => {
      tl.to(q('[data-eyebrow]'), { opacity: 1, y: 0, duration: 0.7 }, 0.2)
        .to(
          q('[data-title] [data-line]'),
          { yPercent: 0, duration: 1.2, ease: 'power4.out', stagger: 0.12 },
          0.3,
        )
        .to(q('[data-lede]'), { opacity: 1, y: 0, duration: 0.7 }, 0.8)
        .to(q('[data-actions]'), { opacity: 1, y: 0, duration: 0.7 }, 0.95)
        .to(q('[data-proof]'), { opacity: 1, y: 0, duration: 0.6 }, 1.1)
        .to(q('[data-cue]'), { opacity: 1, y: 0, duration: 0.6 }, 1.4);
    },
    { start: 'top bottom' },
  );

  return (
    <Section ref={ref} id="hero" padded={false} className="border-t-0">
      <div className="relative grid min-h-svh place-items-center px-[clamp(1.4rem,5vw,5rem)] pb-[clamp(6rem,14vh,8rem)] pt-[clamp(7rem,16vh,10rem)]">
        <Lightfall
          colors={STREAK_COLORS}
          backgroundColor={CANVAS_GROUND}
          mouseInteraction={false}
        />
        <div className="hero-scrim pointer-events-none absolute inset-0" />

        <div className="relative grid w-full max-w-[min(92vw,900px)] justify-items-center gap-6 text-center sm:gap-7">
          <Reveal as="p" data-eyebrow className="hud eyebrow m-0 text-brand-sky">
            {copy.hero.eyebrow}
          </Reveal>
          <LineMask
            as="h1"
            data-title
            className="m-0 text-[clamp(2.25rem,7.2vw,5.75rem)] font-extrabold leading-[0.94] tracking-[-0.05em]"
            lines={[firstLine, secondLine]}
          />
          <Reveal
            as="p"
            data-lede
            className="m-0 max-w-[640px] text-[clamp(1rem,1.7vw,1.2rem)] leading-relaxed text-foreground/80"
          >
            {copy.hero.lede}
          </Reveal>
          <Reveal data-actions className="mt-1 flex flex-wrap items-center justify-center gap-3">
            <Button
              {...sectionLink(copy.hero.primary.id)}
              icon={<ArrowRightIcon size={16} weight="bold" aria-hidden />}
            >
              {copy.hero.primary.label}
            </Button>
            <Button {...sectionLink(copy.hero.secondary.id)} variant="ghost">
              {copy.hero.secondary.label}
            </Button>
          </Reveal>
          {/* The counts behind the promise, from the schedule rather than the copy. */}
          <Reveal data-proof className="mt-3">
            <ul className="hud m-0 flex list-none flex-wrap items-center justify-center gap-x-3 gap-y-2 p-0 text-muted-foreground">
              {copy.hero.proof.map((stat, index) => (
                <li key={stat.id} className="flex items-center gap-3">
                  {index > 0 ? (
                    <span aria-hidden className="h-1 w-1 rounded-full bg-brand-red" />
                  ) : null}
                  <span>
                    <span className="font-semibold text-foreground">{STATS[stat.id]}</span>{' '}
                    {stat.label}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-8 hidden justify-center md:flex">
          <Reveal data-cue className="hud grid justify-items-center gap-3 text-muted-foreground">
            <span>{copy.hero.cue}</span>
            <span className="scrollcue-line" />
          </Reveal>
        </div>
      </div>
    </Section>
  );
}
